import { namingConfig } from "@gap/utils/config/naming-config"
import { canSeeWorkItems } from "@gap/utils/utils"
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined'
import BarChartIcon from '@mui/icons-material/BarChart'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import BusinessOutlined from '@mui/icons-material/BusinessOutlined'
import GroupIcon from '@mui/icons-material/Group'
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined'
import SyncIcon from '@mui/icons-material/Sync'
import MenuBook from '@mui/icons-material/MenuBook'
import DriveFileRenameOutlineOutlined from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined'
import VgcSelectClientModal from "features/vgc/select-client/VgcSelectClientModal"
import CalculateIconOutlined from '@mui/icons-material/CalculateOutlined'
import { SvgIconTypeMap } from "@mui/material/SvgIcon"
import { OverridableComponent } from "@mui/material/OverridableComponent"
import Storefront from '@mui/icons-material/Storefront'
import TaskAlt from '@mui/icons-material/TaskAlt'
import FolderOpen from '@mui/icons-material/FolderOpen'
import { RouteItem, RoutePermission, routes } from "./RouteConfig"
import { ChargebeeFeatureId } from "@gap/utils/chargebee/chargebee-features"
import { canSeeGapAcademy } from "./menuFilter"
import PsychologyOutlined from "@mui/icons-material/PsychologyOutlined"
import LogoDevIcon from '@mui/icons-material/LogoDev'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import QueueIcon from '@mui/icons-material/Queue'
import { queuesPermission } from 'features/queues/queuesPermission'
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined'
import EventOutlined from '@mui/icons-material/EventOutlined'
import LibraryBooksOutlined from '@mui/icons-material/LibraryBooksOutlined'
import SchoolIcon from '@mui/icons-material/School'
import SportsOutlinedIcon from '@mui/icons-material/SportsOutlined'

export type IconType = OverridableComponent<SvgIconTypeMap<{}, "svg">>

export interface MenuSubItem {
   title: string
   /** Nav path passed to routerStore.navigate, e.g. 'templates/meetings' */
   navPath: string
}

export interface MenuChildItem {
   title: string
   icon: IconType
   permission?: RoutePermission
   /** Optional chip rendered on the right of the menu item (ExtendedMenu only) */
   chip?: string
   /** Present on leaf items. Omitted when `children` is set — the item becomes an expandable group instead of navigating. */
   action?: {
      route: RouteItem
   } | {
      modal: React.FunctionComponent
      /** If you set this as '', there wont be a title shown and no X button appears. Setting it to ' ' ensures it does render */
      modalTitle?: string
   } | {
      type: 'sso'
      provider: 'circle'
   }
   /** Present on group items. Renders an expandable sub-menu (sub-items inherit this item's permission). */
   children?: MenuSubItem[]
}

export interface Menu {
   title: string
   children: Array<MenuChildItem>
}

export const menu: Menu[] = [
   {
      title: '',
      children: [
         {
            title: namingConfig.document.plural,
            icon: FolderOpen,
            action: {
               route: routes['documents']
            },
            permission: {
               onlyForOrgs: ['client'],
               featureId: 'documents'
            }
         },
         // Meetings is governed by the 'meetings' feature toggle (now generally available via enabledForAllOrgs).
         // Work Items is preserved only for orgs that existed before the cutoff below — newer orgs only get Meetings.
         {
            title: 'Meetings',
            icon: EventOutlined,
            action: {
               route: routes['meetings']
            },
            permission: {
               onlyForOrgs: ['advisor'],
               featureToggle: 'meetings',
            }
         },
         {
            title: namingConfig.workItem.plural,
            icon: TaskAlt,
            action: {
               route: routes['work-items']
            },
            permission: {
               onlyForOrgs: ['advisor'],
               featureId: 'work-items',
               additionalConditions: ({ userOrg }) => canSeeWorkItems(userOrg),
            }
         },
         {
            title: 'Proposals',
            icon: DriveFileRenameOutlineOutlined,
            action: {
               route: routes['proposals']
            },
            permission: {
               featureId: 'proposals',
               actionId: 'maintain',
               onlyForOrgs: ['advisor'],
               chargebeeFeatureId: ChargebeeFeatureId.proposals
            }
         },
         {
            title: 'Value Gap Calculator',
            icon: CalculateIconOutlined,
            action: {
               modal: VgcSelectClientModal
            },
            permission: {
               onlyForOrgs: ['advisor'],
               featureId: 'vgc',
               chargebeeFeatureId: ChargebeeFeatureId.valueGapCalculator
            }
         }
      ]
   },
   {
      title: 'Learn',
      children: [
         {
            title: 'Gap Academy',
            icon: SchoolIcon,
            action: {
               type: 'sso',
               provider: 'circle'
            },
            permission: {
               onlyForOrgs: ['advisor'],
               additionalConditions: canSeeGapAcademy,
            }
         },
         {
            title: 'Coaching',
            icon: SportsOutlinedIcon,
            action: {
               route: routes['coach']
            },
            permission: {
               onlyForOrgs: ['advisor'],
               featureToggle: 'meetings',
            }
         }
      ]
   },
   {
      title: 'Manage',
      children: [
         {
            title: 'Clients',
            icon: Storefront,
            action: {
               route: routes['clients']
            },
            permission: {
               onlyForOrgs: ['advisor'],
               featureId: 'clients'
            }
         },
         {
            title: 'Templates',
            icon: LibraryBooksOutlined,
            permission: {
               onlyForOrgs: ['advisor'],
               featureId: 'blueprints'
            },
            children: [
               { title: 'Meetings', navPath: 'templates/meetings' },
               { title: 'Documents', navPath: 'templates/documents' },
               { title: 'Messages', navPath: 'templates/messages' },
            ]
         },
         {
            title: 'Services & Pricing',
            icon: LocalOfferOutlined,
            action: {
               route: routes['services']
            },
            permission: {
               onlyForOrgs: ['advisor'],
               featureId: 'services'
            }
         }
      ]
   },
   {
      title: 'Publishing',
      children: [
         {
            title: namingConfig.process.plural,
            icon: AccountTreeOutlined,
            action: {
               route: routes['processes']
            },
            permission: {
               onlyForOrgs: ['gap'],
               featureId: 'processes'
            }
         },
         {
            title: namingConfig.documentTemplate.plural,
            icon: DescriptionOutlined,
            action: {
               route: routes['document-templates']
            },
            permission: {
               onlyForOrgs: ['gap'],
               featureId: 'blueprints'
            }
         },
         {
            title: 'Services',
            icon: MenuBook,
            action: {
               route: routes['services'],
            },
            permission: {
               onlyForOrgs: ['gap'],
               featureId: 'services'
            }
         },
         {
            title: namingConfig.mindset.plural,
            icon: PsychologyOutlined,
            action: {
               route: routes['mindsets']
            },
            permission: {
               onlyForOrgs: ['gap'],
               featureId: 'mindsets'
            }
         },
      ]
   },
   {
      title: 'Support',
      children: [
         {
            title: 'Organisations',
            icon: BusinessOutlined,
            action: {
               route: routes['organisations']
            },
            permission: {
               onlyForOrgs: ['gap'],
               featureId: 'organisations'
            }
         },
         {
            title: 'User Management',
            icon: ManageAccountsIcon,
            action: {
               route: routes['user-management']
            },
            permission: {
               onlyForOrgs: ['gap'],
               featureId: 'user-management'
            }
         }
      ]
   },
   {
      title: 'Admin',
      children: [
         {
            title: 'Roles',
            icon: GroupIcon,
            action: {
               route: routes['roles']
            },
            permission: {
               onlyForOrgs: ['gap'],
               featureId: 'roles'
            }
         },
         {
            title: 'Background Jobs',
            icon: SyncIcon,
            action: {
               route: routes['background-jobs']
            },
            permission: {
               featureId: 'background-jobs',
               onlyForOrgs: ['gap']
            }
         },
         {
            title: 'AI Prompts',
            icon: AutoAwesomeOutlined,
            action: {
               route: routes['ai-prompts']
            },
            permission: {
               featureId: 'admin-settings',
               onlyForOrgs: ['gap']
            }
         },
         {
            title: 'Feature Toggles',
            icon: ToggleOnIcon,
            action: {
               route: routes['feature-toggles']
            },
            permission: {
               onlyForOrgs: ['gap'],
               devOrProductTeamOnly: true
            }
         },
         {
            title: 'Queues',
            icon: QueueIcon,
            action: {
               route: routes['queues']
            },
            permission: queuesPermission
         },
         {
            title: 'Dev Settings',
            icon: LogoDevIcon,
            action: {
               route: routes['dev-settings']
            },
            permission: {
               featureId: 'admin-settings',
               onlyForOrgs: ['gap'],
               developerOnly: true
            }
         },
         {
            title: 'Dev Sandbox',
            icon: BarChartIcon,
            action: {
               route: routes['dev-sandbox']
            },
            permission: {
               onlyForOrgs: ['gap'],
               onlyForStages: ['dryrun', 'dryrun-v3', 'dev', 'preview', 'feature', 'bug']
            }
         }
      ]
   }
]